import React from "react";
import CalculatorModuleExample from "../CalculatorModule/CalculatorExample";
import { lpTrack } from "../../helpers/lpTrack";
import { FORM_CARD_ID } from "../../helpers/paidScroll";

/**
 * Paid-search landing hero (AXA /calcular-seguro-vida-axa/).
 *
 * NEW, page-scoped component — it consumes the SAME data shape as the shared
 * `hero-calculator-image` block and renders the multi-step form by REUSING
 * `CalculatorModuleExample` untouched (the form's HTML/logic/submission are not
 * modified). The shared HeroCalculatorImage / CalculatorModule components stay
 * intact; masterTemplate swaps to this renderer only on the allowlisted LP.
 *
 * Design: full-width brand-blue background (--primary-color, #00008F on AXA),
 * two columns on desktop (copy + trust + phone | form), form-first on mobile so
 * it's visible above the fold (~80% mobile traffic). Brand colors only.
 */

const PHONE_LABEL = "+34 911 982 326";
const PHONE_TEL = "tel:+34911982326";

interface HeroPaidCalculatorProps {
  data: {
    CalculatorModule?: { Url: string; Promo?: string }[];
    HasSeriousIllnesses?: boolean;
    // other hero-calculator-image fields exist but are intentionally unused here
  };
}

const PhoneGlyph = ({ className = "" }: { className?: string }) => (
  <svg
    viewBox="0 0 24 24"
    width="22"
    height="22"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    className={className}
  >
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z" />
  </svg>
);

const HeroPaidCalculator = ({ data }: HeroPaidCalculatorProps) => {
  // Reuse the exact form data mapping the shared hero uses — form untouched.
  const calculatorData = (data?.CalculatorModule || []).map((cm) => ({
    Url: cm.Url,
    Promo: cm.Promo,
    backgroundColor: "#FFFFFF",
    HasPhoneValidation: false,
    HasSeriousIllnesses: data?.HasSeriousIllnesses ?? false,
  }));

  return (
    <section className="relative overflow-hidden bg-[var(--primary-color)]">
      {/* Brand-blue diagonal gradient + slow "aurora" glow. Decorative only;
          keyframes live in <PaidStyles/> and freeze under prefers-reduced-motion. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "linear-gradient(135deg, #00008F 0%, #00006E 55%, #000048 100%)",
        }}
      />
      <div
        aria-hidden="true"
        className="axa-aurora pointer-events-none absolute -inset-[25%]"
        style={{
          background:
            "radial-gradient(38% 48% at 28% 30%, rgba(0,176,255,0.38), transparent 70%), radial-gradient(46% 56% at 76% 64%, rgba(86,96,255,0.42), transparent 72%)",
          filter: "blur(46px)",
          mixBlendMode: "screen",
        }}
      />
      <div className="relative z-10 max-w-content mx-auto px-[24px] py-[40px] md:py-[64px] grid grid-cols-1 md:grid-cols-12 md:gap-x-[48px] gap-[24px] md:items-start">
        {/* Headline block — always first (mobile + desktop top-left) */}
        <div className="order-1 md:col-span-6 md:col-start-1 md:row-start-1">
          <div className="inline-flex items-center gap-[8px] rounded-full bg-white/15 px-[14px] py-[8px]">
            <span className="h-[8px] w-[8px] rounded-full bg-[#FAB71D]" aria-hidden="true" />
            <span className="font-DM font-medium text-white text-[13px] md:text-[14px] leading-tight">
              ¡Oferta! 1€/mes los 3 primeros meses
            </span>
          </div>
          <h1 className="mt-[16px] font-DM font-bold text-white text-[40px] md:text-[56px] leading-[1.08] tracking-[-0.02em]">
            <span className="hidden md:inline">
              Protege a tu familia, incluso cuando ya no estés
            </span>
            <span className="md:hidden">Protege a tu familia desde hoy</span>
          </h1>
        </div>

        {/* Form block — LAST on mobile (after copy + subtitle + trust), right column on desktop */}
        <div className="order-3 md:col-span-6 md:col-start-7 md:row-start-1 md:row-span-2">
          <div
            id={FORM_CARD_ID}
            className="bg-white rounded-[20px] p-[20px] md:p-[24px] shadow-[0_20px_50px_-20px_rgba(0,0,0,0.35)]"
          >
            <p className="font-DM font-bold text-[#424242] text-[20px] md:text-[22px] mb-[12px]">
              Calcula tu seguro de vida
            </p>
            <CalculatorModuleExample data={calculatorData} />
            <p className="mt-[12px] text-center text-[#6b7280] font-DM text-[12px]">
              Gratis · Sin compromiso · Sin tarjeta de crédito
            </p>
          </div>
        </div>

        {/* Details block — subtitle + trust + phone. Desktop: left col under headline. Mobile: between headline and the form. */}
        <div className="order-2 md:col-span-6 md:col-start-1 md:row-start-2">
          <p className="text-white/75 font-DM text-[15px] md:text-[17px] leading-[1.5]">
            Seguro de vida AXA. Sin médicos, sin permanencia.
            <br />
            100% online en 2 minutos.
          </p>
          <div className="mt-[16px] flex flex-wrap items-center gap-x-[12px] gap-y-[4px] text-white/80 font-DM text-[14px]">
            <span className="whitespace-nowrap">
              <span aria-hidden="true" className="text-[#FAB71D]">★★★★★</span> +50.000 familias protegidas
            </span>
            <span className="hidden md:inline text-white/40" aria-hidden="true">
              |
            </span>
            <span className="whitespace-nowrap">🏆 Forbes · EL PAÍS · La Vanguardia</span>
          </div>
          <a
            href={PHONE_TEL}
            onClick={() => lpTrack("phone_click", { location: "hero" })}
            className="hidden md:flex mt-[24px] w-fit items-center gap-[12px] rounded-[16px] border border-white/30 px-[18px] py-[14px] text-white no-underline transition-colors hover:bg-white/10"
          >
            <PhoneGlyph className="text-white shrink-0" />
            <span className="flex flex-col leading-tight">
              <span className="text-white/70 font-DM text-[12px]">
                ¿Prefieres hablar con un experto?
              </span>
              <span className="font-DM font-bold text-[20px]">{PHONE_LABEL}</span>
              <span className="text-white/60 font-DM text-[12px]">
                Lun–Vie · Sin compromiso
              </span>
            </span>
          </a>
        </div>
      </div>
    </section>
  );
};

export default HeroPaidCalculator;
